/**
 * Plugins › Connections › Browsers: which browser web work goes through when
 * a task names none - the user's own or the Denker browser - for every
 * browser the extension runs in, and the Denker browser's row, in either
 * lane as a task may name it: set it up, or open it to sign in to sites
 * (`DenkerBrowserRow`). Rust owns the choice.
 */
import { useCallback, useEffect, useState } from "react";
import { PocketRow, SelectField } from "@/components/ui";
import {
  getBrowserLane,
  setBrowserLane,
  type BrowserLane,
} from "@/desktop/denker-browser";
import { useT } from "@/i18n";
import { settingsBrowserMessages } from "@/i18n/messages/settings-browser";
import type en from "@/i18n/messages/settings-browser/en";
import { DenkerBrowserRow } from "./denker-browser-row";
import { SettingsFieldError } from "./settings-text-field";

const LANES: readonly BrowserLane[] = ["yours", "denker"];

const LANE_KEYS = {
  yours: { label: "laneYours", line: "laneYoursLine" },
  denker: { label: "laneDenker", line: "laneDenkerLine" },
} as const satisfies Record<
  BrowserLane,
  { label: keyof typeof en; line: keyof typeof en }
>;

const isLane = (value: string): value is BrowserLane =>
  (LANES as readonly string[]).includes(value);

const message = (failure: unknown) =>
  failure instanceof Error ? failure.message : String(failure);

function useBrowserLane() {
  const t = useT(settingsBrowserMessages);
  const [lane, setLane] = useState<BrowserLane | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    void getBrowserLane().then(
      (next) => {
        if (current) setLane(next);
      },
      () => {
        if (current) setError(t("laneReadFailed"));
      },
    );
    return () => {
      current = false;
    };
  }, [t]);

  const choose = useCallback(
    async (next: BrowserLane) => {
      if (saving) return;
      setSaving(true);
      setError(null);
      try {
        await setBrowserLane(next);
        setLane(next);
      } catch (failure) {
        setError(message(failure));
      }
      setSaving(false);
    },
    [saving],
  );

  return { lane, saving, error, choose };
}

export function BrowserLaneRows() {
  const t = useT(settingsBrowserMessages);
  const browser = useBrowserLane();
  const errorLine = (
    <SettingsFieldError error={browser.error} testId="browser-lane-error" />
  );
  if (!browser.lane) {
    return browser.error ? (
      <PocketRow
        testId="settings-row-browser-lane"
        title={t("laneTitle")}
        below={errorLine}
      />
    ) : null;
  }
  const laneLine = t(LANE_KEYS[browser.lane].line);
  return (
    <>
      <PocketRow
        testId="settings-row-browser-lane"
        title={t("laneTitle")}
        secondLine={<span title={laneLine}>{laneLine}</span>}
        trailing={
          <SelectField
            variant="value"
            value={browser.lane}
            options={LANES.map((value) => ({
              value,
              label: t(LANE_KEYS[value].label),
            }))}
            disabled={browser.saving}
            onChange={(next) => {
              if (isLane(next)) void browser.choose(next);
            }}
            testId="browser-lane"
            ariaLabel={t("laneTitle")}
          />
        }
        below={errorLine}
      />
      <DenkerBrowserRow />
    </>
  );
}
